import { Controller } from '@hotwired/stimulus';
import { Turbo } from '@hotwired/turbo-rails';
import { MODAL_FRAMES, ModalHistory, modalLocation, modalURL, sameDialog } from '../lib/modal_navigation';

// Coordinates URLs and frame restoration; UTMR remains the dialog owner.
export default class extends Controller {
  connect() {
    this.history = new ModalHistory(Turbo);
    this.requests = new Map();
    this.listeners = [];
    this.location = modalLocation(window.location.href);
    const pop = this.pop.bind(this);
    this.history.intercept(pop);
    this.listeners.push([window, 'popstate', pop]);
    this.listen(document, 'turbo:load', this.loaded);
    this.listen(document, 'turbo:before-fetch-request', this.request);
    this.listen(document, 'submit', this.prepareForm);
    this.listen(document, 'turbo:before-fetch-response', this.response);
    this.listen(document, 'turbo:before-frame-render', this.rendering);
    this.listen(document, 'turbo:frame-render', this.rendered);
    this.listen(document, 'turbo:frame-missing', this.missing);
    this.listen(document, 'turbo:submit-end', this.submitted, false);
    this.listen(document, 'modal:closed', this.closed);
    this.listen(document, 'turbo:before-cache', this.beforeCache);
    this.loaded();
  }

  disconnect() {
    this.listeners.forEach(([target, name, listener, capture = true]) =>
      target.removeEventListener(name, listener, capture)
    );
  }

  listen(target, name, method, capture = true) {
    const listener = method.bind(this);
    target.addEventListener(name, listener, capture);
    this.listeners.push([target, name, listener, capture]);
  }

  beforeCache() {
    this.caching = true;
  }

  loaded() {
    if (document.documentElement.hasAttribute('data-turbo-preview')) return;
    this.caching = false;
    this.location = modalLocation(window.location.href);
    this.restore();
  }

  frame(id) {
    return document.getElementById(id);
  }
  dialog(frame) {
    return frame?.querySelector('dialog.utmr');
  }
  owner(id) {
    return ['modal-inner', 'modal-inner-stacked'].includes(id)
      ? this.frame(id)?.closest('dialog.utmr')?.closest('turbo-frame')?.id || id
      : id;
  }

  navigationDisabled(dialog) {
    return dialog?.querySelector('[data-modal-navigation]')?.dataset.modalNavigation === 'false';
  }

  prepareForm(event) {
    const form = event.target;
    const dialog = form.closest('dialog.utmr');
    if (!dialog || this.navigationDisabled(dialog)) return;
    const target = event.submitter?.dataset.turboFrame || form.dataset.turboFrame;
    if (target === '_top' || form.dataset.turbo === 'false') return;
    if (target && !dialog.contains(this.frame(target))) {
      form.dataset.turboFrame = dialog.closest('turbo-frame').id;
    }
  }

  request(event) {
    const { fetchOptions, url } = event.detail;
    const id = new Headers(fetchOptions.headers).get('Turbo-Frame');
    if (!MODAL_FRAMES.has(id)) return;
    if (event.target instanceof HTMLFormElement) {
      if (this.navigationDisabled(event.target.closest('dialog.utmr'))) return;
      const parent = this.parentURL(id);
      if (fetchOptions.body instanceof FormData || fetchOptions.body instanceof URLSearchParams) {
        fetchOptions.body.set('return_to', `${parent.pathname}${parent.search}${parent.hash}`);
      }
      event.target.dataset.modalReturnTo = parent.href;
      event.target.dataset.modalFrame = this.owner(id);
      if (fetchOptions.method.toUpperCase() !== 'GET') {
        // Fetch preserves this header on the redirect's GET. That response is
        // discarded; subscribed regions receive the committed replacements.
        const headers = new Headers(fetchOptions.headers);
        headers.set('X-Modal-Redirect-Refresh', 'true');
        fetchOptions.headers = headers;
      }
    } else if (event.target.tagName === 'TURBO-FRAME' && fetchOptions.method.toUpperCase() === 'GET') {
      const path = new URL(url);
      if (path.origin !== window.location.origin) return;
      path.searchParams.delete('return_to');
      this.requests.set(id, `${path.pathname}${path.search}${path.hash}`);
    }
  }

  response(event) {
    // UTMR's redirect handler replaces history with empty state and may navigate
    // to the controller's default destination. Keep the submitted parent instead.
    if (!(event.target instanceof HTMLFormElement) || !event.target.dataset.modalReturnTo) return;
    const response = event.detail.fetchResponse;
    if (response.succeeded && response.redirected) {
      event.preventDefault();
      event.target.dataset.modalRedirected = 'true';
    }
  }

  submitted(event) {
    if (!event.detail.success || event.target.dataset.modalRedirected !== 'true') return;
    delete event.target.dataset.modalRedirected;
    const dialog = event.target.closest('dialog.utmr') || this.dialog(this.frame(event.target.dataset.modalFrame));
    const override = event.target.closest('[data-modal-close-on-submit]');
    if (
      override?.dataset.modalCloseOnSubmit === 'false' ||
      dialog?.__ultimateTurboModalController?.closeOnSubmitValue === false
    )
      return;
    dialog?.__ultimateTurboModalController?.hideModal();
  }

  rendered(event) {
    const id = event.target.id;
    if (!MODAL_FRAMES.has(id)) {
      // A campaign-frame visit can change the page without turbo:load.
      if (!this.restoring) this.location = modalLocation(window.location.href);
      return;
    }
  }

  rendering(event) {
    const id = event.target.id;
    if (!MODAL_FRAMES.has(id)) return;
    const url = this.requests.get(id);
    this.requests.delete(id);
    const ownerFrame = this.frame(this.owner(id));
    const dialog = this.dialog(event.detail.newFrame) || this.dialog(ownerFrame);
    const disabled = this.navigationDisabled(dialog);
    if (ownerFrame) ownerFrame.dataset.modalNavigation = String(!disabled);
    if (disabled) {
      // Old shared URLs must not restore a dialog that now opts out of history.
      if (this.restoring) event.detail.newFrame.replaceChildren();
      return;
    }
    if (!url || this.restoring || !dialog) return;
    const previous = new URL(window.location.href);
    const { base, dialogs } = modalLocation(previous.href);
    // Inner-frame pagination is navigation within its owning dialog.
    const owner = this.owner(id);
    const index = dialogs.findIndex((entry) => entry.frame === owner);
    const next = index < 0 ? [...dialogs, { frame: owner, url }] : [...dialogs.slice(0, index), { frame: owner, url }];
    const destination = modalURL(base, next);
    if (destination.href !== previous.href) this.history.write(destination, 'push', previous.href);
    this.location = { base, dialogs: next };
  }

  parentURL(id) {
    const { base, dialogs } = modalLocation(window.location.href);
    const owner = this.owner(id);
    const index = dialogs.findIndex((entry) => entry.frame === owner);
    return modalURL(base, dialogs.slice(0, index < 0 ? -1 : index));
  }

  closed(event) {
    if (this.restoring || this.caching || !MODAL_FRAMES.has(event.target.id)) return;
    if (event.target.dataset.modalNavigation === 'false') return;
    if (!this.location.dialogs.some((entry) => entry.frame === event.target.id)) return;
    const destination = this.parentURL(event.target.id);
    const owned = window.history.state?.modalNavigation;
    if (owned?.parent === destination.href && owned.parentIndex === Turbo.session.history.currentIndex - 1) {
      window.history.back();
    } else {
      this.history.write(destination, 'replace');
      this.location = modalLocation(destination.href);
    }
  }

  pop(event) {
    if (this.reverting) {
      event.stopImmediatePropagation();
      this.reverting = false;
      this.restore();
      return;
    }
    const next = modalLocation(window.location.href);
    if (!event.state?.turbo) return;
    if (!next.dialogs.length && !this.location.dialogs.length) return;
    event.stopImmediatePropagation();
    const url = new URL(window.location.href);
    // Serialize repeated Back/Forward gestures so an in-flight close is not
    // mistaken for a veto and an older restoration cannot overwrite a newer one.
    this.historyNavigation = (this.historyNavigation || Promise.resolve()).then(() =>
      this.navigateHistory(next, url, event)
    );
  }

  async navigateHistory(next, url, event) {
    const previousIndex = Turbo.session.history.currentIndex;
    this.restoring = true;
    let common = 0;
    while (
      next.base === this.location.base &&
      common < this.location.dialogs.length &&
      common < next.dialogs.length &&
      sameDialog(this.location.dialogs[common], next.dialogs[common])
    )
      common++;
    for (const entry of this.location.dialogs.slice(common).reverse()) {
      const dialog = this.dialog(this.frame(entry.frame));
      const controller = dialog?.__ultimateTurboModalController;
      await controller?.hideModalWithPromise({ skipHistoryBack: true });
      if (dialog?.isConnected) {
        this.restoring = false;
        this.reverting = true;
        window.history.go(previousIndex - event.state.turbo.restorationIndex);
        return;
      }
    }
    if (next.base !== this.location.base) {
      this.restoring = false;
      Turbo.session.history.onPopState(event);
      return;
    }
    this.history.adopt(url, event.state);
    this.location = next;
    this.restoring = false;
    await this.restore();
  }

  async restore() {
    if (this.restoring) return;
    this.restoring = true;
    const location = this.location;
    try {
      for (const [index, entry] of location.dialogs.entries()) {
        if (this.location !== location || this.caching) break;
        const frame = this.frame(entry.frame);
        if (!frame) {
          this.unavailable(index);
          break;
        }
        if (this.dialog(frame)) continue;
        frame.src = entry.url;
        await frame.loaded;
        // Stimulus connects dialogs after Turbo has inserted their contents.
        await new Promise((resolve) => requestAnimationFrame(resolve));
        if (this.location !== location || this.caching) break;
        if (frame.dataset.modalNavigation === 'false') {
          this.location.dialogs = this.location.dialogs.slice(0, index);
          this.history.write(modalURL(this.location.base, this.location.dialogs), 'replace');
          break;
        }
        if (!this.dialog(frame)) {
          this.unavailable(index);
          break;
        }
      }
    } finally {
      this.restoring = false;
      if (this.location !== location && !this.caching) this.restore();
    }
  }

  unavailable(index) {
    this.location.dialogs = this.location.dialogs.slice(0, index);
    this.history.write(modalURL(this.location.base, this.location.dialogs), 'replace');
    const notice = document.createElement('p');
    notice.setAttribute('role', 'alert');
    notice.className = 'alert alert-warning';
    notice.textContent = 'This dialog is no longer available. You can reopen it from this page.';
    const parent = this.location.dialogs.at(-1);
    (this.dialog(this.frame(parent?.frame)) || document.querySelector('main') || document.body).append(notice);
  }

  missing(event) {
    if (!MODAL_FRAMES.has(event.target.id)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.restoring) return;
    // A link out of a dialog leaves the modal URL in browser history.
    Turbo.visit(event.detail.response.url);
  }
}
